import { DriveMetadata } from './metadata/DriveMetadata';
import { CloudData } from './metadata/CloudData';
import { SaveStatusEnum } from './enums/save-status.enum';
import { TabService } from '../tab.service';
import { FileActionEnum, MimeTypesEnum, ToastrActionEnum, ToastrStringsEnum } from '../../utils/enums';
import { FileConvertorService } from '../file-convertor/file-convertor-service';
import { ModalWindowService } from '../ui/modal-window.service';
import { DocumentManagerService } from '../document/document-manager.service';
import { ToastrDialogService } from '../toastr-dialog.service';
import { BlobToPdfService } from '../blob-to-pdf.service';
import { TranslateService } from '@ngx-translate/core';
import { FileData } from '../../../models/FileData';
import { CloudFoldersEnum } from '../../utils/enums/cloud-folders.enum';
import { OneDriveItemMetadata } from './one-drive/one-drive.api.service';
import { CloudException, CloudExceptionEnum } from './models/exceptions';
import { files } from 'dropbox';
import {PdfToKesConversion, HtmlToRtfConversion} from '../file-convertor/conversions';
import { AsgardDocPdf } from 'src/app/core/documents/asgard-doc-pdf';
import { AsgardDocFroala } from 'src/app/core/documents/asgard-doc-froala';
import { isK3000domain } from 'src/app/core/common';
import { validateMimetypes } from './common';
import { ExamModeRestrictionsService } from '../exam/exam-mode-restrictions.service';
import { MatDialog } from '@angular/material/dialog';
import { AccountService } from '../auth/account.service';
import { LicenseService } from '../license.service';
import { OpenFileAction, OpenFileService } from '../open-file.service';
import { AsgardDocEpub } from 'src/app/core/documents/asgard-doc-epub';
import { EpubFileService } from '../epub/epub-file.service';

export interface CloudUploadFilePath {
  parentFolderId: string;
  driveId?: string;
}

export class AsgardCloudBaseService {
  ALINEA_AUDIOMAKER_FOLDER: string = 'Alinea Audio';
  K3000_AUDIOMAKER_FOLDER: string = 'K3000user Audio';
  KES_OR_SEK_PATTERN: RegExp = /(\.kes|\.sek)$/i;

  driveMetadata: DriveMetadata | null = null;
  dropboxMetadata: files.FileMetadataReference | files.FolderMetadataReference | files.DeletedMetadataReference | null = null;
  oneDriveMetadata: OneDriveItemMetadata | null = null;

  kesToCloud: boolean;
  saveAs: boolean = true;
  ROOT_FOLDER: string = 'root';

  constructor(
    public tabService: TabService,
    public convertorService: FileConvertorService,
    public modalService: ModalWindowService,
    public docMgrService: DocumentManagerService,
    public toastrService: ToastrDialogService,
    public blobToPdfService: BlobToPdfService,
    public epubFileService: EpubFileService,
    public translateService: TranslateService,
    public examModeService: ExamModeRestrictionsService,
    public dialog: MatDialog,
    public accountService: AccountService,
    public licenseService: LicenseService,
    public openFileService: OpenFileService
  ) {
  }

  async getFiles(_currentFolder: string, _showAllFiles: boolean, _dialogStatus: SaveStatusEnum,
    _folderToShow?: CloudFoldersEnum, _isRedirected?: boolean): Promise<CloudData[]> {
    throw new Error('Not Implemented');
  }

  createFileFromBlob(blob, extension: MimeTypesEnum, filename: string): File {
    return new File(
      [blob], filename,
      { type: extension }
    );
  }

  async processFiles(file: File,
    _dialogStatus: SaveStatusEnum,
    metadata: DriveMetadata | files.FileMetadataReference | files.FolderMetadataReference | files.DeletedMetadataReference | OneDriveItemMetadata,
    paneIndex: number): Promise<void> {

    this.toastrService.showToastrByType(ToastrActionEnum.Success, ToastrStringsEnum.DownloadDone, false, metadata.name);
    this.openFileService.open(
      file,
      _dialogStatus === SaveStatusEnum.Ocr ? OpenFileAction.CLOUDOCR : OpenFileAction.CLOUD,
      metadata,
      paneIndex
    )
  }

  newFolder(_name: string, _cloudData: CloudData | null, _autorename?: boolean): void {
    throw new Error('Not Implemented');
  }

  copyOrMoveFile(_fileCopyInfo): void {
    throw new Error('Not Implemented');
  }

  deleteFile(_fileId): Promise<any> {
    throw new Error('Not Implemented');
  }

  overrideFilenameParams(filename: string): void {
    if (this.tabService.activeTab) {
      this.tabService.activeTab.setTabTitle(filename);
      this.tabService.activeTab.originalFileName = filename;
    }
  }

  updateFile(_file: CloudData, _params: Partial<DriveMetadata>) {
    throw new Error('Not Implemented');
  }

  filterUnsupportedFiles(cloudDataList: CloudData[], dialogStatus: SaveStatusEnum): CloudData[] {
    return cloudDataList
      .filter(cloudData => {
        // to support the new shortcuts
        const mimetype = cloudData.mimeType === MimeTypesEnum.GoogleDriveShortcut
                      ? cloudData.shortcutDetails?.targetMimeType : cloudData.mimeType;
        return validateMimetypes(
          { fileName: cloudData.name, mimeType: mimetype },
          dialogStatus === SaveStatusEnum.Ocr,
          isK3000domain(),
          dialogStatus === SaveStatusEnum.ExamCreationFilePicker || dialogStatus === SaveStatusEnum.ExamModeFilePicker
        );
      });
  }

  async getFileFromCloud(_fileId: string, _dialogStatus: SaveStatusEnum, _paneIndex: number) {
    throw new Error('Not Implemented');
  }

  fillMetadata = (_metadata, tabId: string | null = null) => {
    // console.log('fillMetadata', _metadata);
    if (tabId) {
      const activeTab = this.tabService.getTabById(tabId);
      if (activeTab) {
        activeTab.setMetadata(_metadata);
      }
    } else {
      if (this.tabService.activeTab) {
        this.tabService.activeTab.setMetadata(_metadata);
      }
    }
  };

  saveAsDataBuilder(name: string, parentFolder: CloudData | null): void {
    if (this.getKesToCloud()) {
      // this.convertorService.getPdfAndCreateKes(FileActionEnum.UploadToDrive, name);
      this.convertorService.makeConversion<PdfToKesConversion>(PdfToKesConversion, {
        action: FileActionEnum.UploadToDrive,
        fileName: name
      });
    } else if (this.docMgrService.currentDoc instanceof AsgardDocPdf) {
      this.uploadToCloud(parentFolder, name);
    } else if (this.docMgrService.currentDoc instanceof AsgardDocFroala) {
      // this.convertorService.getHtmlAndCreateRtf(FileActionEnum.UploadToDrive, name);
      this.convertorService.makeConversion<HtmlToRtfConversion>(HtmlToRtfConversion, {
        action: FileActionEnum.UploadToDrive,
        fileName: name
      });
    } else if (this.docMgrService.currentDoc instanceof AsgardDocEpub) {
      this.uploadToCloud(parentFolder, name);
    }
  }

  saveAudiofile(_audiofile: File, _currentFolder: CloudData | null) {
    throw new Error('Not Implemented');
  }

  getTranslatedTitle = (): string => {
    if (this.tabService.activeTab) {
      return this.translateService.instant(this.tabService.activeTab.title) || this.tabService.activeTab.title || '';
    }
    return '';
  };

  async uploadToCloud(_path: CloudData | null, _filename?: string, _file?: File) {
    throw new Error('Not Implemented');
  }

  checkFilenameBeforeUpload = (file: File | null, filename: string, parent: string): FileData => {
    let fileData: FileData;
    let newFile;
    // if (file && file.type !== MimeTypesEnum.Mp3) {
    //   if (file.type === MimeTypesEnum.ApplicationPdf && filename.indexOf('pdf') < 0) {
    //     filename += '.pdf';
    //     newFile = new File([file], file.name + '.pdf', { type: file.type });
    //   }
    // }

    fileData = {
      file: newFile ? newFile : file,
      filename,
      path: parent
    };

    return fileData;
  };

  uploadFile(_autorename: boolean) {
    throw new Error('Not Implemented');
  }

  checkIfFileIsTrashedOrGone() {
    throw new Error('Not Implemented');
  }

  setSaveAs = (isSaveAs: boolean) => {
    this.saveAs = isSaveAs;
  };

  getSaveAs = (): boolean => {
    return this.saveAs;
  };

  setKesToCloud = (isKes2Cloud: boolean) => {
    this.kesToCloud = isKes2Cloud;
  };

  getKesToCloud = (): boolean => {
    return this.kesToCloud;
  };

  checkFileMovingPath(fileParentId: string, path: string): void {
    if (fileParentId === path) {
      throw new CloudException(CloudExceptionEnum.fileMove);
    }
  }

  createOrAddAudiomakerFolder(_parent?, _folder?) {
    throw new Error('Not Implemented');
  }

  getFileFromId(_fileId: string): Promise<File> {
    throw new Error('Not Implemented');
  }

  displayError(error: any): void {
    let messageKey = error;
    if (error instanceof CloudException) {
      messageKey = error.toString();
    }
    // this.modalService.createModalWindow(CommonMessageDialogComponent, { messageKey });
    this.toastrService.showToaster(ToastrActionEnum.Error, this.translateService.instant(messageKey));
  }
}
